<template>
    <div class="border-stroke-1 relative flex min-h-0 flex-1 flex-col border-t">
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto w-full overflow-x-hidden">
            <div class="flex shrink-0 items-start">
                <component
                    :is="playerComponent"
                    :index="0"
                    :player="competitor1"
                    :team="competitor1"
                    :active-profile-id="activeProfileId"
                />
                <div class="bg-stroke-1 relative h-full w-px"></div>
                <component
                    :is="playerComponent"
                    :index="1"
                    :player="competitor2"
                    :team="competitor2"
                    :active-profile-id="activeProfileId"
                />
            </div>

            <div
                class="grid w-full gap-3 gap-x-6 gap-y-4 p-4"
                :class="numPlayers > 2 ? `` : 'grid-cols-[1fr_min-content_1fr]'"
                :style="
                    numPlayers > 2
                        ? `grid-template-columns: repeat(${numPlayers}, 1fr) min-content`
                        : ''
                "
            >
                <div
                    class="grid grid-cols-subgrid"
                    :class="`col-span-${numPlayers + 1}`"
                    v-for="value in cricketNumbers"
                    :key="value"
                    :data-testid="`cricket-target-${value}`"
                >
                    <div
                        class="relative flex items-center justify-between"
                        v-for="playerIndex in numPlayers"
                        :key="playerIndex"
                        :class="[
                            playerIndex == 2 && numPlayers == 2
                                ? 'order-last flex-row-reverse'
                                : '',
                            { 'opacity-50': !isPlayerCurrentTurn(playerIndex) },
                        ]"
                    >
                        <div
                            v-for="dot in 3"
                            :key="dot"
                            class="aspect-square w-1/4 rounded-full"
                            :class="getDotClass(value, dot, playerIndex)"
                        />
                        <div
                            v-if="isTargetBlocked(value, playerIndex)"
                            class="pointer-events-none absolute inset-0 flex items-center justify-center"
                        >
                            <div class="h-1 w-full bg-white"></div>
                        </div>
                    </div>
                    <div class="flex items-center gap-3">
                        <div
                            class="flex-1 p-1 text-center text-sm font-bold uppercase transition-colors"
                            :class="getInputButtonClass(value)"
                            v-for="multiplier in value == 'B' ? ['S', 'D'] : ['S', 'D', 'T']"
                            :key="multiplier"
                            data-testid="cricket-score-button"
                            :data-value="value"
                            :data-multiplier="multiplier"
                            @click="
                                canAddScore && !isTargetClosed(value) && addScore(value, multiplier)
                            "
                        >
                            {{
                                value == 'B'
                                    ? multiplier === 'S'
                                        ? $t('cricket.outer_bull_short')
                                        : $t('cricket.bull_short')
                                    : `${multiplier}${value}`
                            }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="bg-bg flex shrink-0 flex-col">
            <div class="border-stroke-1 flex flex-col gap-1 border-t p-4">
                <h3>{{ $t('cricket.earned_points') }}</h3>
                <div class="grid w-full grid-cols-3 gap-3">
                    <DiagonalCutButton
                        v-for="index in 3"
                        :key="index"
                        variant="secondary"
                        :cut-position="
                            index === 1 ? 'right-top' : index > 2 ? 'left-top' : 'both-top'
                        "
                        class="flex-1"
                        fill-color="bg-card"
                    >
                        <template v-if="scores[index - 1]">
                            <span class="text-red-600">{{
                                scores[index - 1].dart_type === DART_TYPES.MISS
                                    ? $t('cricket.miss')
                                    : scores[index - 1].dart_type === DART_TYPES.OUTER_BULL
                                      ? $t('cricket.outer_bull_short')
                                      : scores[index - 1].dart_type === DART_TYPES.BULL
                                        ? $t('cricket.bull_short')
                                        : scores[index - 1].dart_type + scores[index - 1].value
                            }}</span>
                            &nbsp;
                            {{ getScorePoints(scores[index - 1]) }}
                        </template>
                        <template v-else>
                            <span class="text-red-600">-</span>
                        </template>
                    </DiagonalCutButton>
                </div>
            </div>

            <div class="flex gap-4 p-4">
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    class="min-w-0 flex-1"
                    :disabled="!isEditingMode && !hasLastTurn"
                    @click="isEditingMode ? undoLastScore() : loadLastTurn()"
                >
                    <span class="min-w-0 truncate text-lg">{{ isEditingMode ? $t('cricket.undo') : $t('cricket.redo') }}</span>
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="both-top"
                    class="min-w-0 flex-1"
                    data-testid="cricket-miss"
                    @click="addMiss"
                >
                    <span class="min-w-0 truncate text-lg">{{ $t('cricket.miss') }}</span>
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    class="relative min-w-0 flex-1"
                    :disabled="scores.length < 3 || isSubmitting"
                    data-testid="cricket-submit"
                    @click="submitTurn"
                >
                    <span
                        v-if="isSubmitting"
                        class="absolute inset-0 flex items-center justify-center"
                    >
                        <svg
                            class="h-5 w-5 animate-spin text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                    </span>
                    <span :class="{ 'opacity-0': isSubmitting }" class="min-w-0 truncate text-lg">{{ $t('cricket.submit') }}</span>
                </DiagonalCutButton>
            </div>
        </div>
    </div>
</template>

<script>
import { useMatchStore } from '@/stores/match.js'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import PlayPlayer from '@/components/PlayPlayer.vue'
import PlayTeam from '@/components/PlayTeam.vue'
import { DART_TYPES, DART_TYPE_FIXED_VALUES } from '@/constants/dartTypes.js'
import { useToast } from '@/composables/useToast.js'

export default {
    name: 'CricketGame',
    components: { DiagonalCutButton, PlayPlayer, PlayTeam },
    emits: ['quit-match'],
    data() {
        return {
            scores: [],
            numPlayers: 2,
            isSubmitting: false,
            isSubmittingDart: false,
            turnPlayerId: null,
            DART_TYPES,
            DART_TYPE_FIXED_VALUES,
        }
    },
    watch: {
        'matchStore.editingTurn'(newEditingTurn) {
            if (newEditingTurn && newEditingTurn.darts && newEditingTurn.darts.length > 0) {
                this.turnPlayerId = this.currentPlayer.id
                this.scores = newEditingTurn.darts.map((dart) => ({
                    value: dart.dart_number,
                    dart_type: dart.dart_type,
                }))
            }
        },
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        isTeamPlay() {
            return this.matchStore.isTeamPlay
        },
        playerComponent() {
            return this.isTeamPlay ? 'PlayTeam' : 'PlayPlayer'
        },
        profiles() {
            return this.matchStore.getProfiles
        },
        teams() {
            return this.matchStore.getTeams
        },
        competitor1() {
            if (this.isTeamPlay) {
                return this.teams[0] || null
            }
            return this.profiles[0] || null
        },
        competitor2() {
            if (this.isTeamPlay) {
                return this.teams[1] || null
            }
            return this.profiles[1] || null
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        // While editing a turn the gatekeeper already points at the next player,
        // so the edited turn's owner is the one whose darts are being entered.
        activeProfileId() {
            return this.matchStore.editingTurn?.profile_id ?? this.currentPlayer?.id ?? null
        },
        matchData() {
            return this.matchStore.getMatchData
        },
        player1SetsWon() {
            return this.calculatePlayerSetsWon(this.competitor1?.team)
        },
        player2SetsWon() {
            return this.calculatePlayerSetsWon(this.competitor2?.team)
        },
        player1LegsWon() {
            return this.calculatePlayerLegsWon(this.competitor1?.team)
        },
        player2LegsWon() {
            return this.calculatePlayerLegsWon(this.competitor2?.team)
        },
        cricketNumbers() {
            const lowerBound = this.matchData?.settings?.lower_bound || 15
            const numbers = []

            for (let i = 20; i >= lowerBound; i--) {
                numbers.push(i)
            }

            numbers.push('B')

            return numbers
        },
        canAddScore() {
            return !this.isSubmittingDart && this.scores.length < 3
        },
        hasLastTurn() {
            const currentLeg = this.matchStore.getCurrentLeg
            if (!currentLeg?.turns?.length) return false

            const completedTurns = currentLeg.turns.filter(
                (turn) =>
                    turn.thrown_score !== undefined &&
                    turn.thrown_score !== -1 &&
                    !this.matchStore.isTurnFromBot(turn),
            )
            return completedTurns.length > 0
        },
        isEditingMode() {
            return this.scores.length > 0
        },
    },
    methods: {
        addScore(value, multiplier) {
            if (!this.canAddScore) return

            if (this.scores.length === 0) {
                this.turnPlayerId = this.currentPlayer.id
            }

            let dartType = multiplier
            let dartValue = value

            if (value === 'B') {
                if (multiplier === 'S') {
                    dartType = DART_TYPES.OUTER_BULL
                    dartValue = DART_TYPE_FIXED_VALUES[DART_TYPES.OUTER_BULL]
                } else {
                    dartType = DART_TYPES.BULL
                    dartValue = DART_TYPE_FIXED_VALUES[DART_TYPES.BULL]
                }
            }

            this.scores.push({ value: dartValue, dart_type: dartType })
        },
        undoLastScore() {
            this.scores.pop()
            if (this.scores.length === 0) {
                this.turnPlayerId = null
            }
        },
        loadLastTurn() {
            const currentLeg = this.matchStore.getCurrentLeg
            if (!currentLeg?.turns?.length) return

            const completedTurns = currentLeg.turns.filter(
                (turn) =>
                    turn.thrown_score !== undefined &&
                    turn.thrown_score !== -1 &&
                    !this.matchStore.isTurnFromBot(turn),
            )

            if (completedTurns.length === 0) return

            const hasSeenRedoTooltip =
                localStorage.getItem('dartshopper-redo-tooltip-seen') === 'true'
            if (!hasSeenRedoTooltip) {
                const { addToast } = useToast()
                addToast(
                    this.$t('cricket.editing_turn'),
                    5000,
                    this.$t('cricket.editing_hint'),
                )
                localStorage.setItem('dartshopper-redo-tooltip-seen', 'true')
            }

            const lastTurn = completedTurns[completedTurns.length - 1]

            if (!lastTurn.darts || lastTurn.darts.length === 0) return

            const competitorId = lastTurn.team
            const competitorTurns = currentLeg.turns.filter((turn) => turn.team === competitorId)
            const turnIndex = competitorTurns.findIndex((t) => t === lastTurn)

            this.scores = lastTurn.darts.map((dart) => ({
                value: dart.dart_number,
                dart_type: dart.dart_type,
            }))

            this.matchStore.setEditingTurn(competitorId, turnIndex, {
                score: lastTurn.thrown_score,
                darts: lastTurn.darts || [],
            })
        },
        addMiss() {
            if (!this.canAddScore) return

            if (this.scores.length === 0) {
                this.turnPlayerId = this.currentPlayer.id
            }

            this.scores.push({ value: 0, dart_type: DART_TYPES.MISS })
        },
        async submitTurn() {
            if (this.scores.length < 3) {
                console.log('Need 3 darts to submit turn')
                return
            }

            this.isSubmitting = true
            this.isSubmittingDart = true

            try {
                console.log('Submitting cricket turn with scores:', this.scores)

                const profileId = this.matchStore.editingTurn
                    ? this.matchStore.editingTurn.profile_id
                    : this.turnPlayerId || this.currentPlayer.id

                if (this.matchStore.editingTurn) {
                    for (let i = 0; i < this.scores.length; i++) {
                        const score = this.scores[i]
                        const dartIndex = i

                        await this.matchStore.updateDart({
                            profile_id: profileId,
                            dart_type: score.dart_type,
                            dart_number: parseInt(score.value) || 0,
                            dart_index: dartIndex,
                        })
                    }
                } else {
                    for (let i = 0; i < this.scores.length; i++) {
                        const score = this.scores[i]
                        const dartIndex = i + 1

                        await this.matchStore.submitDart({
                            profile_id: profileId,
                            dart_type: score.dart_type,
                            dart_number: parseInt(score.value) || 0,
                            dart_index: dartIndex,
                        })
                    }
                }

                console.log('Cricket turn submitted successfully')
                this.scores = []
                this.turnPlayerId = null
                this.matchStore.clearEditingTurn()
            } catch (error) {
                console.error('Failed to submit cricket turn:', error)
            } finally {
                this.isSubmitting = false
                this.isSubmittingDart = false
            }
        },
        calculatePlayerSetsWon(playerId) {
            if (!playerId || !this.matchData?.sets) return 0

            let setsWon = 0
            for (const set of this.matchData.sets) {
                if (set.winner_id === playerId) {
                    setsWon++
                }
            }
            return setsWon
        },
        calculatePlayerLegsWon(playerId) {
            if (!playerId || !this.matchData?.sets) return 0

            let legsWon = 0
            for (const set of this.matchData.sets) {
                if (set.legs) {
                    for (const leg of set.legs) {
                        if (leg.winner_id === playerId) {
                            legsWon++
                        }
                    }
                }
            }
            return legsWon
        },
        getCompetitorCricketData(playerIndex) {
            const competitor = playerIndex === 1 ? this.competitor1 : this.competitor2
            if (!competitor || !this.matchData?.sets) return null

            const sets = this.matchData.sets
            if (!sets.length) return null

            const currentSet = sets[sets.length - 1]
            if (!currentSet?.legs?.length) return null

            const currentLeg = currentSet.legs[currentSet.legs.length - 1]
            if (!currentLeg?.turns?.length) return null

            // While editing, show the state from before the edited turn so the
            // darts being re-entered aren't counted on top of the old ones.
            const editingTurn = this.matchStore.editingTurn
            const turns = editingTurn
                ? currentLeg.turns.slice(0, editingTurn.turn_index)
                : currentLeg.turns

            const competitorTurns = turns.filter((turn) => turn.team === competitor.team)
            if (!competitorTurns.length) return null

            const latestTurn = competitorTurns[competitorTurns.length - 1]
            return latestTurn?.state || null
        },
        getDotClass(value, dot, playerIndex) {
            const cricketData = this.getCompetitorCricketData(playerIndex)

            const targetKey = value === 'B' ? '25' : value.toString()
            const points = cricketData?.points?.[targetKey] || 0

            if (points >= dot) {
                return 'bg-green-500'
            }

            return 'bg-zinc-700'
        },
        isTargetBlocked(value, playerIndex) {
            const cricketData = this.getCompetitorCricketData(playerIndex)
            if (!cricketData?.points) return false

            const targetKey = value === 'B' ? '25' : value.toString()
            const points = cricketData.points[targetKey] || 0

            return points >= 3
        },
        getPlayerByIndex(playerIndex) {
            return playerIndex === 1 ? this.competitor1 : this.competitor2
        },
        isTargetClosed(value) {
            const player1Blocked = this.isTargetBlocked(value, 1)
            const player2Blocked = this.isTargetBlocked(value, 2)
            return player1Blocked && player2Blocked
        },
        getInputButtonClass(value) {
            const isClosed = this.isTargetClosed(value)

            if (isClosed) {
                return 'bg-zinc-800 border-2 border-zinc-700 text-titles cursor-not-allowed'
            }

            return 'bg-zinc-900 border-2 border-zinc-700 text-white cursor-pointer hover:bg-zinc-800'
        },
        getScorePoints(score) {
            const activeProfileId = this.activeProfileId
            if (!activeProfileId) return '+0'

            if (score.dart_type === DART_TYPES.MISS) {
                return '+0'
            }
            let playerIndex = 1
            if (this.isTeamPlay) {
                if (this.competitor2?.players?.some((player) => player.id === activeProfileId)) {
                    playerIndex = 2
                }
            } else {
                if (activeProfileId === this.competitor2?.id) {
                    playerIndex = 2
                }
            }

            const opponentIndex = playerIndex === 1 ? 2 : 1

            const cricketData = this.getCompetitorCricketData(playerIndex)
            const opponentCricketData = this.getCompetitorCricketData(opponentIndex)

            const targetKey =
                score.dart_type === DART_TYPES.OUTER_BULL || score.dart_type === DART_TYPES.BULL
                    ? DART_TYPE_FIXED_VALUES[DART_TYPES.OUTER_BULL].toString()
                    : score.value.toString()
            const currentMarks = cricketData?.points?.[targetKey] || 0
            const opponentMarks = opponentCricketData?.points?.[targetKey] || 0

            if (opponentMarks >= 3) {
                return '+0'
            }

            let multiplierValue = 1
            if (score.dart_type === DART_TYPES.DOUBLE) multiplierValue = 2
            else if (score.dart_type === DART_TYPES.TRIPLE) multiplierValue = 3
            else if (score.dart_type === DART_TYPES.BULL) multiplierValue = 2

            const baseValue =
                score.dart_type === DART_TYPES.OUTER_BULL || score.dart_type === DART_TYPES.BULL
                    ? DART_TYPE_FIXED_VALUES[DART_TYPES.OUTER_BULL]
                    : score.value

            const targetValue =
                score.dart_type === DART_TYPES.OUTER_BULL || score.dart_type === DART_TYPES.BULL
                    ? DART_TYPE_FIXED_VALUES[DART_TYPES.OUTER_BULL]
                    : score.value
            const previousHitsInTurn = this.scores.filter((s, index) => {
                const sTarget =
                    s.dart_type === DART_TYPES.OUTER_BULL || s.dart_type === DART_TYPES.BULL
                        ? DART_TYPE_FIXED_VALUES[DART_TYPES.OUTER_BULL]
                        : s.value
                return sTarget === targetValue && index < this.scores.indexOf(score)
            })

            let cumulativeMarks = currentMarks
            for (const hit of previousHitsInTurn) {
                if (hit.dart_type === DART_TYPES.DOUBLE) cumulativeMarks += 2
                else if (hit.dart_type === DART_TYPES.TRIPLE) cumulativeMarks += 3
                else if (hit.dart_type === DART_TYPES.OUTER_BULL) cumulativeMarks += 1
                else if (hit.dart_type === DART_TYPES.BULL) cumulativeMarks += 2
                else cumulativeMarks += 1
            }

            const marksAfterThisHit = cumulativeMarks + multiplierValue

            if (marksAfterThisHit <= 3) {
                return '+0'
            }

            if (cumulativeMarks >= 3) {
                const points = baseValue * multiplierValue
                return `+${points}`
            } else {
                const scoringMarks = marksAfterThisHit - 3
                const points = baseValue * scoringMarks
                return `+${points}`
            }
        },
        isPlayerCurrentTurn(playerIndex) {
            const activeProfileId = this.activeProfileId
            const competitor = this.getPlayerByIndex(playerIndex)

            if (!activeProfileId || !competitor) return false

            if (this.isTeamPlay) {
                return competitor.players?.some((player) => player.id === activeProfileId)
            }

            return activeProfileId === competitor.id
        },
    },
}
</script>
